import { Style } from "app/shared/model/app.model";
import { Report } from "ng/src/app/reports/report.model";
import { AuditedEntity } from "app/shared/model/app.model";
import { SelfCreatedUpdatedBy, HREFTemplated, Self } from "app/shared/model/link.model";
import { NameDescSortId } from "app/shared/model/properties.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import { Note } from "../notes/shared/note.model";
import { Template, TemplateWithDrafts } from 'ng/src/app/templates/shared/template.model';
import { LogBookUser, LogUser } from "ng/src/app/users/shared/user.model";
import { GeneralParameters } from "app/shared/model/request-parameter.model";

export type LogBookLog = Log;
export interface Log extends AuditedEntity {
	adminRequiredForTagEdit?: boolean;
	defaultCommentsToShow?: number;
	defaultMaxNoteHeight?: number;
	defaultReportFilter?: Report;
	defaultTemplate?: Template;
	deletionScheduledTime?: string;
	description?: string;
	id: number;
	lastNote?: Note;
	logOwner: LogOwner;
	millisToEvidentPaymentExpiration?: number;
	millisToFeatureExpiration: {
		[featureName: string]: number;
	};
	millisToLogDeletion?: number;
	millisToPaymentExpiration: number;
	millisToSentinelPaymentExpiration?: number;
	name: string;
	newNoteStyles: Style[];
	notesNewMinutes?: number;
	oopsEditMinutes?: number;
	signInTag?: TagValue;
	signOutTag?: TagValue;
	timeZone: string;
	_links: SelfCreatedUpdatedBy & {
		defaultReportObjectFilter: HREFTemplated;
		defaultTemplate: HREFTemplated;
		log: HREFTemplated;
		logOwner: HREFTemplated;
		newNoteStyles: HREFTemplated;
		signInTag: HREFTemplated;
		signOutTag: HREFTemplated;
	};
}

export interface LogWithLogOwnerUser extends Log {
	logOwnerUser: LogBookUser
}

export interface LogWithTemplates extends Log {
	templates: TemplateWithDrafts[];
}

export interface LogWithData extends Log {
	activeLogUser: LogUser;
	isWriteOnShiftRole?: boolean;
	logConfigHref: { [key: string]: string };
	logConfigs: { [key: string]: string };
	onsiteEnabled: boolean;
	onsiteNumberProvisioned: boolean;
	role: 'WRITE' | 'READ' | 'ADMIN';
	sentinelProvisioned: boolean;
	templates: Template[];
	userDefaultTemplate?: Template;
	userRole: LogRole;
}

export type LogBase = Omit<Log,
	'_links' |
	'createdBy' |
	'defaultReportFilter' |
	'defaultTemplate' |
	'logOwner' |
	'newNoteStyles' |
	'updatedBy' |
	'signInTag' |
	'signOutTag'
>

export interface LogRole extends NameDescSortId {
	id: LogRoleId
	_links: Self & {
		logRole: HREFTemplated
	};
}

export interface LogConfig {
	id: number;
	dataType: NameDescSortId;
	hierarchy: string | 'logbook.webui.config';
	key: string;
	logId: number;
	value: string;
	provision?: true;
	_links: SelfCreatedUpdatedBy & {
		dataType: HREFTemplated;
		log: HREFTemplated;
		logConfigs: HREFTemplated;
	};
}

export enum DataType {
	BOOLEAN = 1,
	SHORT_TEXT = 2,
	LONG_TEXT = 3
}

export module DataType {
	export function toURI(type: DataType) {
		return `/api/dataTypes/${type}`;
	}
}

interface LogOwner {
	created: string;
	evidentPaymentExpires?: string;
	featureExpiration: {
		[featureName: string] : string;
	}
	paymentExpires: string;
	sentinelPaymentExpires?: string;
	updated: string;
}

export interface LogQuery extends GeneralParameters {
	nameLike?: string;
	memberOfLogAsRole?: LogRoleId
}

export enum LogRoleId {
	'READ' = 1,
	'WRITE_ALWAYS' = 2,
	'ADMIN' = 3,
	'WRITE_ONSHIFT' = 4
}